// src/components/customTopics/CustomTopicRow.tsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomTopicModel } from "../../models/CustomTopicModel";
import { useTranslation } from "react-i18next";
import { FaLock } from "react-icons/fa";
// Renders the category's iconKey (from the curated topic-category icon set).
// TOPIC_CATEGORY_ICON_MAP is used to skip rendering the icon box entirely for
// unknown keys — an empty box used to shift the row's title to the right.
import { TopicCategoryIconByKey, TOPIC_CATEGORY_ICON_MAP } from "../../utils/topicCategoryIconMap";
import { getRoleI18nKey, getNickaltiOwnerMark } from "../../utils/userStatusUtils";
import { USER_STATUS_ICON_MAP } from "../../utils/userStatusIconMap";
import "./CustomTopicRow.css";

import { customTopicUrl } from "../../utils/canonicalUrl";
interface CustomTopicRowProps {
  topic: CustomTopicModel;
  isSelected: boolean;
  onSelect: () => void;
  // Called on mobile after navigation so App.tsx can switch to the entries pane
  onMobileNavigate?: () => void;
}

// Same heat thresholds as ClubRow/MatchRow so the badge colours stay consistent
const getHeatClass = (count: number): string => {
  if (count === 0)  return "heat-0";
  if (count < 3)    return "heat-1";
  if (count < 8)    return "heat-2";
  if (count < 20)   return "heat-3";
  if (count < 50)   return "heat-4";
  return "heat-5";
};

const formatCount = (count: number): string => {
  if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(".", ",") + "m";
  if (count >= 1_000)     return (count / 1_000).toFixed(1).replace(".", ",") + "b";
  return String(count);
};

export const CustomTopicRow: React.FC<CustomTopicRowProps> = ({ topic, isSelected, onSelect, onMobileNavigate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const recentCount = topic.recentEntryCount ?? 0;

  // Primary channel ("kanal") = index 0 of the ordered channels list — drives
  // the icon next to the title. The tooltip lists ALL channels of the topic.
  const primaryChannel = topic.channels && topic.channels.length > 0 ? topic.channels[0] : null;
  const channelNames = topic.channels && topic.channels.length > 0
    ? topic.channels.map((c) => c.name).join(", ")
    : undefined;

  // Nickaltı owner lifecycle marker — null for normal topics and active owners.
  // Resolved here rather than inline so the icon lookup happens once per row.
  const ownerMark = getNickaltiOwnerMark(topic.ownerStatus, topic.ownerClosed);
  const OwnerMarkIcon = ownerMark ? USER_STATUS_ICON_MAP[ownerMark.iconKey] : null;

  const handleClick = () => {
    onSelect();
    navigate(customTopicUrl(topic.slug, topic.id));
    // On mobile: signal App.tsx to switch from the list pane to the content pane
    onMobileNavigate?.();
  };

  return (
    <div
      className={`topic-row ${isSelected ? "selected" : ""} ${topic.isLocked ? "locked" : ""}`}
      onClick={handleClick}
    >
      {/* Title — optionally preceded by the PRIMARY channel's icon (index 0 of
          the ordered channels list), if one is set. Unknown icon keys skip the
          box entirely (no empty-space misalignment). The tooltip lists ALL
          channels of the topic (hashtag semantics — a topic can have up to 10). */}
      <div className="topic-row__info">
        <div className="topic-row__title">
          {primaryChannel?.iconKey && TOPIC_CATEGORY_ICON_MAP[primaryChannel.iconKey] && (
            <span className="topic-row__cat-icon" title={channelNames}>
              <TopicCategoryIconByKey iconKey={primaryChannel.iconKey} size={12} />
            </span>
          )}
          {topic.title}
          {/* Nickaltı topics: append the owner's localized role, e.g. "caylak1 (çaylak)",
              so they are visually distinct from normal topics in the list */}
          {topic.ownerRole && (
            <span className="topic-row__nickalti-suffix">
              {" "}({t(getRoleI18nKey(topic.ownerRole))})
            </span>
          )}
          {/* Nickaltı owner marker — suspended, frozen, banned, being deleted,
              closed, removed or dormant. The topic survives all of these, so
              without the marker the row looks like a live author's. */}
          {ownerMark && OwnerMarkIcon && (
            <span
              className={`topic-row__owner-mark topic-row__owner-mark--${ownerMark.modifier}`}
              title={t(ownerMark.tooltipKey)}
              aria-label={t(ownerMark.tooltipKey)}
            >
              <OwnerMarkIcon size={11} />
            </span>
          )}
        </div>
      </div>

      {/* Right: entry count badge + lock icon (always rightmost) */}
      <div className="topic-row__right">
        <span
          className={`topic-row__entry-badge ${getHeatClass(recentCount)}`}
          title={t("topic_row_recent_entries", "Son 24 saatte entry")}
        >
          {formatCount(recentCount)}
        </span>
        {topic.isLocked && (
          <span className="row-lock-icon" title={t("topic_locked_tooltip")}>
            <FaLock size={10} />
          </span>
        )}
      </div>
    </div>
  );
};